/**
 * Shared widgets and constants for Access Control V3 admin screens.
 * The level ladder mirrors the backend: 0=None 1=View 2=Standard 3=Admin.
 */

import React from 'react';
import { authenticatedApiCall } from '../../utils/apiClient';

export const LEVELS = [
  { value: 0, key: 'none', label: 'None', color: '#95a5a6', hint: 'Module hidden completely' },
  { value: 1, key: 'view', label: 'View', color: '#3498db', hint: 'Can open and read' },
  { value: 2, key: 'standard', label: 'Standard', color: '#2ecc71', hint: 'Can work in it (edit / enter data)' },
  { value: 3, key: 'admin', label: 'Admin', color: '#e74c3c', hint: 'Can configure it; includes every extra' },
];

export const levelMeta = (value) => LEVELS[value] || LEVELS[0];

/** Build a simple api(method, url, data) function bound to the MSAL session. */
export const makeApi = (instance, accounts) => async (method, url, data = null) => {
  const response = await authenticatedApiCall(instance, accounts, method, url, data);
  if (response?.data?.success === false) {
    throw new Error(response.data.error || 'Request failed');
  }
  return response.data;
};

export const LevelBadge = ({ level, small = false }) => {
  const meta = levelMeta(level);
  return (
    <span title={meta.hint} style={{
      display: 'inline-block', padding: small ? '1px 8px' : '3px 12px', borderRadius: 12,
      backgroundColor: `${meta.color}18`, color: meta.color, border: `1px solid ${meta.color}50`,
      fontSize: small ? 11 : 12, fontWeight: 700, whiteSpace: 'nowrap',
    }}>{meta.label}</span>
  );
};

/** Four-state segmented control: None / View / Standard / Admin. */
export const LevelSegmented = ({ value, onChange, disabled = false }) => (
  <div style={{ display: 'inline-flex', border: '1px solid #dee2e6', borderRadius: 8, overflow: 'hidden' }}>
    {LEVELS.map(l => {
      const active = value === l.value;
      return (
        <button
          key={l.value}
          type="button"
          disabled={disabled}
          onClick={() => !disabled && onChange(l.value)}
          title={l.hint}
          style={{
            padding: '5px 12px', border: 'none', fontSize: 12, fontWeight: active ? 700 : 500,
            cursor: disabled ? 'not-allowed' : 'pointer',
            backgroundColor: active ? l.color : '#fff',
            color: active ? '#fff' : disabled ? '#ccc' : '#666',
            opacity: disabled && !active ? 0.6 : 1,
            transition: 'background-color 0.1s',
          }}
        >
          {l.label}
        </button>
      );
    })}
  </div>
);

export const GranularChip = ({ label, active, implied, onClick, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={implied || !onClick}
    title={title}
    style={{
      padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
      cursor: onClick && !implied ? 'pointer' : 'default',
      backgroundColor: implied ? '#e74c3c12' : active ? '#2ecc7118' : '#f1f3f5',
      color: implied ? '#e74c3c' : active ? '#1e7e34' : '#868e96',
      border: `1px solid ${implied ? '#e74c3c40' : active ? '#2ecc7150' : '#dee2e6'}`,
      margin: '2px 4px 2px 0',
    }}
  >
    {implied ? '✓ ' : active ? '✓ ' : ''}{label}
  </button>
);

export const sectionCard = {
  backgroundColor: '#fff', border: '1px solid #e9ecef', borderRadius: 12,
  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
};

export const inputStyle = {
  padding: '7px 10px', border: '1px solid #ced4da', borderRadius: 6, fontSize: 13,
  backgroundColor: '#fff',
};

export const primaryBtn = (disabled = false) => ({
  padding: '8px 18px', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600,
  backgroundColor: disabled ? '#adb5bd' : '#007bff', color: '#fff',
  cursor: disabled ? 'not-allowed' : 'pointer',
});

export const subtleBtn = {
  padding: '8px 14px', border: '1px solid #ced4da', borderRadius: 6, fontSize: 13,
  backgroundColor: '#fff', color: '#495057', cursor: 'pointer',
};

export const dangerLink = {
  background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: 12, padding: 4,
};

export const errorBanner = (msg) => msg ? (
  <div style={{
    margin: '0 0 12px', padding: '10px 14px', borderRadius: 8, fontSize: 13,
    backgroundColor: '#f8d7da', border: '1px solid #f5c2c7', color: '#842029',
  }}>{msg}</div>
) : null;
